import { Grid, TextField } from '@mui/material'
import { useTranslate } from '@tolgee/react'
import { useMinimalsTheme } from '@tom-ui/ui'
import { IFormStore, useRegisterSubFormStore } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import {
  StowagePositionFormData,
  StowagePositionSubFormStore,
} from '../stores/StowagePositionSubFormStore'

export type StowagePositionSubFormProps<T> = {
  formStore: IFormStore<T>
  name: keyof T
  initialValue?: StowagePositionFormData
}

export const StowagePositionSubForm = observer(
  <T,>({ formStore, name, initialValue }: StowagePositionSubFormProps<T>) => {
    const theme = useMinimalsTheme()
    const { t } = useTranslate()

    const store = useRegisterSubFormStore(formStore, name, new StowagePositionSubFormStore())

    useEffect(() => {
      store.setValues(initialValue)
    }, [initialValue, store])

    return (
      <Grid container spacing={theme.customSpacing.s}>
        <Grid
          size={{
            xs: 12,
            md: 4,
          }}
        >
          <TextField
            {...store.bay.props}
            data-cy='stowage-position-bay'
            fullWidth
            required
            onChange={event => {
              const digitsOnly = event.target.value.replace(/\D/g, '').slice(0, 3)
              store.bay.onChange({
                ...event,
                target: { ...event.target, value: digitsOnly },
              } as React.ChangeEvent<HTMLInputElement>)
            }}
            onBlur={event => {
              const raw = event.target.value.replace(/[^0-9]/g, '')
              const padded = raw ? raw.padStart(3, '0') : ''
              store.bay.onChange({
                ...event,
                target: { ...event.target, value: padded },
              } as React.ChangeEvent<HTMLInputElement>)
            }}
            label={t('bay', 'Bay')}
            error={store.bay.isTouched && !!store.bay.error}
            helperText={store.bay.isTouched && t(store.bay.error ?? '')}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 4,
          }}
        >
          <TextField
            {...store.row.props}
            data-cy='stowage-position-row'
            fullWidth
            required
            onChange={event => {
              const digitsOnly = event.target.value.replace(/\D/g, '').slice(0, 2)
              store.row.onChange({
                ...event,
                target: { ...event.target, value: digitsOnly },
              } as React.ChangeEvent<HTMLInputElement>)
            }}
            onBlur={event => {
              const raw = event.target.value.replace(/[^0-9]/g, '')
              const padded = raw ? raw.padStart(2, '0') : ''
              store.row.onChange({
                ...event,
                target: { ...event.target, value: padded },
              } as React.ChangeEvent<HTMLInputElement>)
            }}
            label={t('row', 'Row')}
            error={store.row.isTouched && !!store.row.error}
            helperText={store.row.isTouched && t(store.row.error ?? '')}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 4,
          }}
        >
          <TextField
            {...store.tier.props}
            data-cy='stowage-position-tier'
            fullWidth
            required
            onChange={event => {
              const digitsOnly = event.target.value.replace(/\D/g, '').slice(0, 2)
              store.tier.onChange({
                ...event,
                target: { ...event.target, value: digitsOnly },
              } as React.ChangeEvent<HTMLInputElement>)
            }}
            onBlur={event => {
              const raw = event.target.value.replace(/[^0-9]/g, '')
              const padded = raw ? raw.padStart(2, '0') : ''
              store.tier.onChange({
                ...event,
                target: { ...event.target, value: padded },
              } as React.ChangeEvent<HTMLInputElement>)
            }}
            label={t('tier', 'Tier')}
            error={store.tier.isTouched && !!store.tier.error}
            helperText={store.tier.isTouched && t(store.tier.error ?? '')}
          />
        </Grid>
      </Grid>
    )
  },
)
